import React, {
  FC,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
  useContext,
  useRef,
  useState,
} from "react";
import {
  Form,
  InputNumber,
  Row,
  Col,
  Table,
  Select,
  Tooltip,
  Popconfirm,
  Modal,
  Button,
} from "antd";
import {
  InfoCircleOutlined,
  UpCircleTwoTone,
  DownCircleTwoTone,
  WarningOutlined,
  DeleteOutlined,
  RightCircleTwoTone,
  PlusOutlined,
} from "@ant-design/icons";
import type { ColumnType } from "antd/lib/table";
import { PriceInput, PercentageInput } from "../input-number";
import { TimeDurationInput } from "../time-duration-input";
import { Work, GpmType, GpmThreshold, TeamMembership } from "../../data-types";
import { Rate, FeeEarnerLevel, PricingTarget, LeverageType } from "~/design-system";
import styles from "./top-down-pricing.css";
import { useGpmSettingsCheck } from "~/hooks";
import { GpmOrSlider } from "../gpm-slider";
import { GlobalContext } from "~/context/global-context";
import clsx from "clsx";
import { debounce } from "lodash";
import { useTaxonomyLabel } from "~/context/taxonomy-context";
import { FeeEarnerAndRatesCascaderContainer } from "~/containers/fee-earner-and-rates-cascader-container";
import { RowRateSelect } from "./row-rate-select";

const {
  ["container"]: containerClassName,
  ["header"]: headerClassName,
  ["form-container"]: formContainerClassName,
  ["form"]: formClassName,
  ["focus-info-container"]: focusInfoContainerClassName,
  ["focus-info"]: focusInfoClassName,
  ["focus-item"]: focusItemClassName,
  ["focus-number"]: focusNumberClassName,
  ["table-container"]: tableContainerClassName,
  ["table"]: tableClassName,
  ["table-cell"]: tableCellClassName,
  ["select"]: selectClassName,
  ["good"]: goodClassName,
  ["warning"]: warningClassName,
  ["danger"]: dangerClassName,
  ["gpm-slider"]: gpmSliderClassName,
  ["rate-column"]: rateColumnClassName,
  ["rate-column-header"]: rateColumnHeaderClassName,
  ["rate-column-title"]: rateColumnTitleClassName,
  ["rate-column-info-text"]: rateColumnInfoTextClassName,
} = styles as any;

type AdjustToTargetButtonProps = {
  pricingTarget: PricingTarget;
  onClick: () => void;
  invert?: boolean;
  style?: React.CSSProperties;
  additionalTooltipMessage?: string;
};

type RecalculateHoursFromWorkEffortButtonProps = {
  onClick: () => void;
  style?: React.CSSProperties;
  tooltipMessage?: string;
};

type TopDownPricingProps = {
  currency: { symbol: string; code: string };
  matterId: number;
  clientId?: number;
  organisationalUnitId?: number;
  gpmThreshold: GpmThreshold;
  feeEarnerLevels: FeeEarnerLevel[];
  topDownRates: TeamMembership[];
  topDownWork: Work[];
  topDownRateOptions: {
    feeEarnerLevelId: number;
    order: number;
    rates: Pick<Rate, "id" | "description" | "amount">[];
  }[];
  pricingTarget: PricingTarget;
  topDownFeeTarget: number;
  onTopDownFeeTargetChange: (feeTarget: number | null) => void;
  topDownHoursTarget: number;
  onTopDownHoursTargetChange: (hoursTarget: number | null) => void;
  topDownRateDiscount: number;
  onTopDownRateDiscountChange: (rateDiscount: number | null) => void;
  onFeeEarnerLevelRateChange: (
    oldRateId: number,
    newRateId: number,
    workId: number | null
  ) => Promise<void>;
  onFeeEarnerRateChange?: (
    feeEarnerId: number,
    oldRateId: number,
    newRateId: number,
    workId: number | null
  ) => Promise<void>;
  onRemoveFeeEarnerLevel: (teamMembershipId: number, workIds: number[]) => Promise<void>;
  onAddFeeEarnerLevel: (
    feeEarnerLevelId: number
  ) => Promise<{ teamMembershipId?: number; work?: Work }>;
  onAddFeeEarner?: (
    feeEarnerId: number | null,
    feeEarnerLevelId: number | null,
    rateId: number
  ) => Promise<{ teamMembershipId?: number; work?: Work }>;
  onUpdateWork: (data: { workId: number; timeSingle: number }) => void;
  onUpdateManyWorkHours: (
    data: { workId: number; hours: number; workEffort: number; rateDiscount: number }[]
  ) => void;
  loadingRates: boolean;
  refreshTrigger: number;
};

type TableRowData = TeamMembership & {
  workId: number | null;
  rateDiscount: number;
  discountedRate: number;
  hours: number;
  percentageOfTarget: number;
  workEffort?: number;
  feeTotal: number;
  cost: number;
  costRate: number;
  gpm?: number;
  pendingUpdate: boolean;
};

type TopDownState = {
  tableData: TableRowData[];
  topDownFeeTarget: number;
  topDownHoursTarget: number;
  topDownRateDiscount: number;
  largestRateMinute: number;
  leverageType: LeverageType;
  blendedRate: number;
  pendingUpdateMany: boolean;
  initialised: boolean;
};

type TopDownAction =
  | { type: "UPDATE_TABLE_DATA"; payload: TableRowData[] }
  | { type: "UPDATE_HOURS"; payload: { id: number; hours: number; workId: number | null } }
  | {
      type: "UPDATE_PERCENTAGE_OF_TARGET";
      payload: { id: number; percentageOfTarget: number; workId: number | null };
    }
  | { type: "UPDATE_WORK_EFFORT"; payload: { id: number; workEffort: number | null | undefined } }
  | { type: "CLEAR_WORK_EFFORTS" }
  | { type: "UPDATE_FEE_TARGET"; payload: number }
  | { type: "UPDATE_HOURS_TARGET"; payload: number }
  | { type: "UPDATE_RATE_DISCOUNT"; payload: number }
  | { type: "UPDATE_ROW_RATE_DISCOUNT"; payload: { id: number; rateDiscount: number } }
  | { type: "UPDATE_RATE"; payload: { id: number; rate: Rate } }
  | { type: "ADJUST_ALL_HOURS_TO_TARGET" }
  | { type: "RECALCULATE_HOURS_FROM_WORK_EFFORT" }
  | {
      type: "ADD_FEE_EARNER_LEVEL";
      payload: { rate: Rate; work?: Work; teamMembershipId?: number };
    }
  | { type: "REMOVE_FEE_EARNER_LEVEL"; payload: number }
  | { type: "CLEAR_PENDING_UPDATE"; payload: { id: number } }
  | { type: "CLEAR_PENDING_UPDATE_MANY" }
  | { type: "UPDATE_LARGEST_RATE_MINUTE"; payload: number }
  // | { type: "UPDATE_LEVERAGE_TYPE"; payload: LeverageType }
  | { type: "UPDATE_BLENDED_RATE"; payload: number }
  | {
      type: "APPLY_INITIAL_DATA";
      payload: {
        tableData: TableRowData[];
        blendedRate: number;
        topDownFeeTarget: number;
        topDownHoursTarget: number;
        topDownRateDiscount: number;
        initialised: boolean;
      };
    }
  | { type: "RESET_FOR_REINITIALISE" }
  | { type: "UPDATED_INITIALISED"; payload: { initialised: boolean; state?: TopDownState } };

const isWithinOnePercentOfTarget = (value: number, target: number): boolean => {
  const difference = Math.abs(value - target);
  const percentDifference = (difference / target) * 100;
  return percentDifference <= 1;
};

const calculatePercentageOfTarget = (
  hours: number,
  feeTotal: number,
  pricingTarget: PricingTarget,
  topDownHoursTarget: number,
  topDownFeeTarget: number
) => {
  if (pricingTarget == PricingTarget.Hours) {
    return Number(((100 * hours) / (60 * topDownHoursTarget)).toFixed(1));
  } else {
    if (topDownFeeTarget === 0) return 0;
    return Number(((100 * feeTotal) / topDownFeeTarget).toFixed(1));
  }
};

const calculateHours = (
  percentageOfTarget: number,
  discountedRate: number,
  pricingTarget: PricingTarget,
  topDownHoursTarget: number,
  topDownFeeTarget: number
) => {
  if (pricingTarget === PricingTarget.Fee && topDownFeeTarget === 0) {
    return 0;
  }
  return pricingTarget === PricingTarget.Hours
    ? Number(((60 * percentageOfTarget * topDownHoursTarget) / 100).toFixed(0))
    : Number(((60 * topDownFeeTarget * percentageOfTarget) / (100 * discountedRate)).toFixed(0));
};

const calculateHoursFromWorkEffort = (
  tableData: TableRowData[],
  pricingTarget: PricingTarget,
  topDownFeeTarget: number,
  prevBlendedRate: number,
  rowId?: number,
  workEffort?: number | null
) => {
  // check if all work efforts are zero or undefined
  const allWorkEffortsZero = tableData.every(
    (row) => (row.id !== rowId && !row.workEffort) || (row.id === rowId && !workEffort)
  );
  if (allWorkEffortsZero) {
    return {
      tableData: tableData.map((row) => ({
        ...row,
        workEffort: undefined,
      })),
      blendedRate: prevBlendedRate,
    };
  }
  // first get the new blended rate and implied total hours
  const { totalWeightedRate, totalWorkEffort } = tableData.reduce(
    (acc, row) => ({
      totalWeightedRate:
        acc.totalWeightedRate +
        row.discountedRate *
          (rowId && workEffort !== undefined && row.id === rowId
            ? workEffort ?? 0
            : row.workEffort ?? 0),
      totalWorkEffort:
        acc.totalWorkEffort +
        (rowId && workEffort !== undefined && row.id === rowId
          ? workEffort ?? 0
          : row.workEffort ?? 0),
    }),
    { totalWeightedRate: 0, totalWorkEffort: 0 }
  );
  const blendedRate = totalWorkEffort > 0 ? totalWeightedRate / totalWorkEffort : 0;
  const impliedTotalHours = blendedRate > 0 ? topDownFeeTarget / blendedRate : 0;

  // then get the new table data
  const newTableData = tableData.map((row) => {
    const newHours = Math.round(
      60 *
        impliedTotalHours *
        (rowId && workEffort !== undefined && row.id === rowId
          ? (workEffort ?? 0) / 100
          : (row.workEffort ?? 0) / 100)
    );
    const newFeeTotal = (row.discountedRate * newHours) / 60;
    const newCost = (row.costRate * newHours) / 60;
    return {
      ...row,
      workEffort:
        rowId && workEffort !== undefined && row.id === rowId ? workEffort : row.workEffort,
      hours: newHours,
      feeTotal: newFeeTotal,
      cost: newCost,
      gpm: ((newFeeTotal - newCost) / newFeeTotal) * 100,
      percentageOfTarget: calculatePercentageOfTarget(
        newHours,
        newFeeTotal,
        pricingTarget,
        impliedTotalHours,
        topDownFeeTarget
      ),
    } as TableRowData;
  });
  return {
    tableData: newTableData,
    blendedRate,
  };
};

const calculateWorkEffortFromHours = (
  tableData: TableRowData[],
  pricingTarget: PricingTarget,
  topDownFeeTarget: number,
  topDownHoursTarget: number,
  rowId?: number,
  hours?: number | null
) => {
  // first get the blended rate from the hours
  const { totalWeightedRate, totalHours } = tableData.reduce(
    (acc, row) => {
      const rowHours = row.id === rowId ? hours : row.hours;
      return {
        totalWeightedRate: acc.totalWeightedRate + row.discountedRate * (rowHours ?? 0),
        totalHours: acc.totalHours + (rowHours ?? 0),
      };
    },
    { totalWeightedRate: 0, totalHours: 0 }
  );
  const blendedRate = totalHours > 0 ? totalWeightedRate / totalHours : 0;

  const newTableData = tableData.map((row) => {
    const newHours = row.id === rowId ? Math.round(hours ?? 0) : row.hours ?? 0;
    const newWorkEffort =
      pricingTarget === PricingTarget.Fee
        ? topDownFeeTarget === 0
          ? 0
          : (100 * ((blendedRate * newHours) / 60)) / topDownFeeTarget
        : (100 * newHours) / (60 * topDownHoursTarget);
    const newFeeTotal = (row.discountedRate * newHours) / 60;
    const newCost = (row.costRate * newHours) / 60;
    return {
      ...row,
      workEffort: newWorkEffort,
      hours: newHours,
      feeTotal: newFeeTotal,
      cost: newCost,
      gpm: ((newFeeTotal - newCost) / newFeeTotal) * 100,
      percentageOfTarget: calculatePercentageOfTarget(
        newHours,
        newFeeTotal,
        pricingTarget,
        pricingTarget === PricingTarget.Hours ? topDownHoursTarget : totalHours,
        topDownFeeTarget
      ),
    } as TableRowData;
  });

  return {
    tableData: newTableData,
    blendedRate,
  };
};

function pricingReducer(pricingTarget: PricingTarget) {
  return (state: TopDownState, action: TopDownAction) => {
    switch (action.type) {
      case "UPDATE_TABLE_DATA":
        return {
          ...state,
          tableData: action.payload,
        };
      case "UPDATE_HOURS": {
        const { tableData: updatedTableData, blendedRate: newBlendedRate } =
          calculateWorkEffortFromHours(
            state.tableData,
            pricingTarget,
            state.topDownFeeTarget,
            state.topDownHoursTarget,
            action.payload.id,
            action.payload.hours
          );
        return {
          ...state,
          tableData: updatedTableData,
          blendedRate: newBlendedRate,
          pendingUpdateMany: true,
        };
      }
      case "UPDATE_PERCENTAGE_OF_TARGET": {
        const affectedRow = state.tableData.find((row) => row.id === action.payload.id);
        const newHours = calculateHours(
          action.payload.percentageOfTarget,
          affectedRow?.discountedRate ?? 0,
          pricingTarget,
          state.topDownHoursTarget,
          state.topDownFeeTarget
        );
        const { tableData: updatedTableData, blendedRate: newBlendedRate } =
          calculateWorkEffortFromHours(
            state.tableData,
            pricingTarget,
            state.topDownFeeTarget,
            state.topDownHoursTarget,
            action.payload.id,
            newHours
          );

        return {
          ...state,
          tableData: updatedTableData,
          blendedRate: newBlendedRate,
          pendingUpdateMany: true,
        };
      }
      case "UPDATE_WORK_EFFORT": {
        const { tableData, blendedRate } = calculateHoursFromWorkEffort(
          state.tableData,
          pricingTarget,
          state.topDownFeeTarget,
          state.blendedRate,
          action.payload.id,
          action.payload.workEffort
        );
        return {
          ...state,
          tableData,
          blendedRate,
          pendingUpdateMany: true,
        };
      }
      case "CLEAR_WORK_EFFORTS": {
        const newTableData = state.tableData.map((row) => ({
          ...row,
          workEffort: undefined,
        }));
        return {
          ...state,
          tableData: newTableData,
        };
      }
      case "UPDATE_FEE_TARGET": {
        const newTableData = state.tableData.map((row) => ({
          ...row,
          percentageOfTarget: action.payload !== 0 ? (100 * row.feeTotal) / action.payload : 0,
        }));
        const { tableData: updatedTableData, blendedRate } = calculateWorkEffortFromHours(
          newTableData,
          pricingTarget,
          action.payload,
          state.topDownHoursTarget
        );
        return {
          ...state,
          tableData: updatedTableData,
          topDownFeeTarget: action.payload,
          blendedRate,
          pendingUpdateMany: true,
        };
      }
      case "UPDATE_HOURS_TARGET": {
        const newTableData = state.tableData.map((row) => ({
          ...row,
          percentageOfTarget: action.payload !== 0 ? (100 * row.hours) / (action.payload * 60) : 0,
        }));
        const { tableData: updatedTableData, blendedRate } = calculateWorkEffortFromHours(
          newTableData,
          pricingTarget,
          state.topDownFeeTarget,
          action.payload
        );
        return {
          ...state,
          tableData: updatedTableData,
          topDownHoursTarget: action.payload,
          blendedRate,
          pendingUpdateMany: true,
        };
      }
      case "UPDATE_RATE_DISCOUNT": {
        const newTableData = state.tableData.map((row) => {
          const discountedRate = row.rate.amount * (1 - action.payload / 100);
          const feeTotal = (discountedRate * row.hours) / 60;
          return {
            ...row,
            rateDiscount: action.payload,
            discountedRate,
            feeTotal,
            percentageOfTarget:
              pricingTarget === PricingTarget.Fee
                ? (100 * feeTotal) / state.topDownFeeTarget
                : row.percentageOfTarget,
            gpm: ((feeTotal - row.cost) / feeTotal) * 100,
          };
        });
        if (state.leverageType === LeverageType.WorkEffort) {
          const updatedTableData = calculateHoursFromWorkEffort(
            newTableData,
            pricingTarget,
            state.topDownFeeTarget,
            state.blendedRate * (1 - action.payload / 100) // in case all work efforts are undefined, this will return the discounted blended rate
          );
          return {
            ...state,
            tableData: updatedTableData.tableData,
            blendedRate: updatedTableData.blendedRate,
            topDownRateDiscount: action.payload,
            pendingUpdateMany: true,
          };
        }
        const { totalWeightedRate, totalHours } = newTableData.reduce(
          (acc, row) => ({
            totalWeightedRate: acc.totalWeightedRate + row.discountedRate * row.hours,
            totalHours: acc.totalHours + row.hours,
          }),
          { totalWeightedRate: 0, totalHours: 0 }
        );
        const newBlendedRate = totalHours > 0 ? totalWeightedRate / totalHours : 0;
        return {
          ...state,
          tableData: newTableData,
          topDownRateDiscount: action.payload,
          blendedRate: newBlendedRate,
          pendingUpdateMany: true,
        };
      }
      case "UPDATE_ROW_RATE_DISCOUNT": {
        const newTableData = state.tableData.map((row) => {
          if (row.id === action.payload.id) {
            const discountedRate = row.rate.amount * (1 - action.payload.rateDiscount / 100);
            const feeTotal = (discountedRate * row.hours) / 60;
            return {
              ...row,
              rateDiscount: action.payload.rateDiscount,
              discountedRate,
              feeTotal,
              percentageOfTarget:
                pricingTarget === PricingTarget.Fee
                  ? (100 * feeTotal) / state.topDownFeeTarget
                  : row.percentageOfTarget,
              gpm: ((feeTotal - row.cost) / feeTotal) * 100,
            };
          }
          return row;
        });
        if (state.leverageType === LeverageType.WorkEffort) {
          const updatedTableData = calculateHoursFromWorkEffort(
            newTableData,
            pricingTarget,
            state.topDownFeeTarget,
            state.blendedRate * (1 - state.topDownRateDiscount / 100) // in case all work efforts are undefined, this will return the discounted blended rate
          );
          return {
            ...state,
            tableData: updatedTableData.tableData,
            blendedRate: updatedTableData.blendedRate,
            pendingUpdateMany: true,
          };
        }
        const { totalWeightedRate, totalHours } = newTableData.reduce(
          (acc, row) => ({
            totalWeightedRate: acc.totalWeightedRate + row.discountedRate * row.hours,
            totalHours: acc.totalHours + row.hours,
          }),
          { totalWeightedRate: 0, totalHours: 0 }
        );
        const newBlendedRate = totalHours > 0 ? totalWeightedRate / totalHours : 0;
        return {
          ...state,
          tableData: newTableData,
          blendedRate: newBlendedRate,
          pendingUpdateMany: true,
        };
      }
      case "UPDATE_RATE": {
        const newTableData = state.tableData.map((row) => {
          if (row.id === action.payload.id) {
            const discountedRate =
              action.payload.rate.amount * (1 - state.topDownRateDiscount / 100);
            const newFeeTotal = (discountedRate * row.hours) / 60;
            const newCost = (row.costRate * row.hours) / 60;
            return {
              ...row,
              rate: action.payload.rate,
              rateId: action.payload.rate.id,
              discountedRate,
              feeTotal: newFeeTotal,
              cost: newCost,
              gpm: ((newFeeTotal - newCost) / newFeeTotal) * 100,
              percentageOfTarget:
                pricingTarget === PricingTarget.Fee
                  ? (100 * newFeeTotal) / state.topDownFeeTarget
                  : row.percentageOfTarget,
            };
          }
          return row;
        });
        if (state.leverageType === LeverageType.WorkEffort) {
          const updatedTableData = calculateHoursFromWorkEffort(
            newTableData as TableRowData[],
            pricingTarget,
            state.topDownFeeTarget,
            state.blendedRate
          );
          return {
            ...state,
            tableData: updatedTableData.tableData,
            blendedRate: updatedTableData.blendedRate,
            pendingUpdateMany: true,
          };
        }
        return {
          ...state,
          tableData: newTableData,
        };
      }
      case "ADJUST_ALL_HOURS_TO_TARGET": {
        const totalFee = state.tableData.reduce((acc, row) => acc + row.feeTotal, 0);
        const totalHours = state.tableData.reduce((acc, row) => acc + row.hours, 0);
        const factorToReduce =
          pricingTarget === PricingTarget.Fee
            ? state.topDownFeeTarget / totalFee
            : (60 * state.topDownHoursTarget) / totalHours;
        const newTableData = state.tableData.map((row) => ({
          ...row,
          hours: row.hours * factorToReduce,
        }));
        const { tableData: updatedTableData, blendedRate: newBlendedRate } =
          calculateWorkEffortFromHours(
            newTableData,
            pricingTarget,
            state.topDownFeeTarget,
            state.topDownHoursTarget
          );
        return {
          ...state,
          tableData: updatedTableData,
          blendedRate: newBlendedRate,
          pendingUpdateMany: true,
        };
      }
      case "RECALCULATE_HOURS_FROM_WORK_EFFORT": {
        const { tableData, blendedRate } = calculateHoursFromWorkEffort(
          state.tableData,
          pricingTarget,
          state.topDownFeeTarget,
          state.blendedRate
        );
        return {
          ...state,
          tableData,
          blendedRate,
          pendingUpdateMany: true,
        };
      }
      case "ADD_FEE_EARNER_LEVEL": {
        const newTableData = [
          ...state.tableData,
          {
            id: action.payload.teamMembershipId,
            rate: action.payload.rate,
            rateId: action.payload.rate.id,
            workId: action.payload.work?.id,
            hours: 0,
            percentageOfTarget: 0,
            feeTotal: 0,
            cost: 0,
            costRate: action.payload.work?.costRate,
            discountedRate: action.payload.rate.amount * (1 - state.topDownRateDiscount / 100),
            gpm: 0,
            pendingUpdate: false,
          } as TableRowData,
        ];
        return {
          ...state,
          tableData: newTableData,
        };
      }
      case "REMOVE_FEE_EARNER_LEVEL": {
        const newTableData = state.tableData.filter((row) => row.id !== action.payload);
        if (state.leverageType === LeverageType.WorkEffort) {
          const updatedTableData = calculateHoursFromWorkEffort(
            newTableData,
            pricingTarget,
            state.topDownFeeTarget,
            state.blendedRate
          );
          return {
            ...state,
            tableData: updatedTableData.tableData,
            blendedRate: updatedTableData.blendedRate,
          };
        }
        return {
          ...state,
          tableData: newTableData,
        };
      }
      case "UPDATE_LARGEST_RATE_MINUTE":
        return {
          ...state,
          largestRateMinute: action.payload,
        };
      case "APPLY_INITIAL_DATA": {
        return {
          ...state,
          tableData: action.payload.tableData,
          blendedRate: action.payload.blendedRate,
          topDownFeeTarget: action.payload.topDownFeeTarget,
          topDownHoursTarget: action.payload.topDownHoursTarget,
          topDownRateDiscount: action.payload.topDownRateDiscount,
          pendingUpdateMany: false,
          initialised: action.payload.initialised,
        };
      }
      case "CLEAR_PENDING_UPDATE":
        return {
          ...state,
          tableData: state.tableData.map((row) =>
            row.id === action.payload.id ? { ...row, pendingUpdate: false } : row
          ),
        };
      case "CLEAR_PENDING_UPDATE_MANY":
        return {
          ...state,
          pendingUpdateMany: false,
        };
      case "RESET_FOR_REINITIALISE":
        return {
          ...state,
          initialised: false,
          pendingUpdateMany: false,
          tableData: state.tableData.map((row) => ({
            ...row,
            pendingUpdate: false,
          })),
        };
      // case "UPDATE_LEVERAGE_TYPE": {
      //   return {
      //     ...state,
      //     leverageType: action.payload,
      //   };
      // }
      case "UPDATE_BLENDED_RATE":
        return {
          ...state,
          blendedRate: action.payload,
        };
      case "UPDATED_INITIALISED": {
        if (!action.payload.initialised && action.payload.state) {
          return action.payload.state;
        }
        return {
          ...state,
          initialised: action.payload.initialised,
        };
      }
      default:
        return state;
    }
  };
}

export const TopDownPricing: FC<TopDownPricingProps> = ({
  currency,
  matterId,
  clientId,
  organisationalUnitId,
  gpmThreshold,
  feeEarnerLevels,
  topDownRates,
  topDownWork,
  topDownRateOptions,
  pricingTarget,
  topDownFeeTarget,
  onTopDownFeeTargetChange,
  topDownHoursTarget,
  onTopDownHoursTargetChange,
  topDownRateDiscount,
  onTopDownRateDiscountChange,
  onFeeEarnerLevelRateChange,
  onRemoveFeeEarnerLevel,
  onAddFeeEarnerLevel,
  onAddFeeEarner,
  onUpdateWork,
  onUpdateManyWorkHours,
  loadingRates,
  refreshTrigger,
  onFeeEarnerRateChange,
}) => {
  const { decimalTimeInput: decimal } = useContext(GlobalContext);

  // State for fee earner cascader and modal
  const [selectedFeeEarnerId, setSelectedFeeEarnerId] = useState<number | null>(null);
  const [selectedFeeEarnerLevelId, setSelectedFeeEarnerLevelId] = useState<number | null>(null);
  const [selectedRateId, setSelectedRateId] = useState<number | null>(null);
  const [isAddRowModalVisible, setIsAddRowModalVisible] = useState(false);

  const initialState: TopDownState = {
    tableData: [],
    topDownFeeTarget: 0,
    topDownHoursTarget: 0,
    topDownRateDiscount: 0,
    largestRateMinute: 0,
    leverageType: LeverageType.FeePercentage,
    blendedRate: 0,
    pendingUpdateMany: false,
    initialised: false,
  };

  const [state, dispatch] = useReducer(pricingReducer(pricingTarget), initialState);

  const prevTopDownRatesRef = useRef(topDownRates);
  const prevTopDownWorkRef = useRef(topDownWork);
  const initialisedRef = useRef(state.initialised);
  const pendingTargetsRef = useRef<{
    fee: number | null;
    hours: number | null;
    rateDiscount: number | null;
  }>({ fee: null, hours: null, rateDiscount: null });

  useEffect(() => {
    // Force reinitialisation if topDownRates have changed
    const ratesChanged =
      topDownRates.length !== prevTopDownRatesRef.current.length ||
      JSON.stringify(topDownRates) !== JSON.stringify(prevTopDownRatesRef.current);
    const workChanged =
      topDownWork.length !== prevTopDownWorkRef.current.length ||
      JSON.stringify(topDownWork) !== JSON.stringify(prevTopDownWorkRef.current);

    if (!state.initialised || ratesChanged || workChanged) {
      const pendingTargets = pendingTargetsRef.current;
      const keepFeeTarget =
        pendingTargets.fee !== null && topDownFeeTarget !== pendingTargets.fee;
      const keepHoursTarget =
        pendingTargets.hours !== null && topDownHoursTarget !== pendingTargets.hours;
      const keepRateDiscount =
        pendingTargets.rateDiscount !== null &&
        topDownRateDiscount !== pendingTargets.rateDiscount;

      const effectiveTopDownFeeTarget = keepFeeTarget
        ? pendingTargets.fee ?? topDownFeeTarget
        : topDownFeeTarget;
      const effectiveTopDownHoursTarget = keepHoursTarget
        ? pendingTargets.hours ?? topDownHoursTarget
        : topDownHoursTarget;
      const effectiveTopDownRateDiscount = keepRateDiscount
        ? pendingTargets.rateDiscount ?? topDownRateDiscount
        : topDownRateDiscount;

      if (!keepFeeTarget) pendingTargets.fee = null;
      if (!keepHoursTarget) pendingTargets.hours = null;
      if (!keepRateDiscount) pendingTargets.rateDiscount = null;

      let totalWeightedRate = 0;
      let totalHours = 0;

      const remainingWorks = [...topDownWork];

      const consumeMatchingWork = (rate: TeamMembership) => {
        const rateFeeEarnerId = rate.feeEarner?.id ?? rate.rate?.feeEarner?.id ?? null;
        const rateLevelId = rate.rate?.feeEarnerLevel?.id ?? rate.rate?.feeEarnerLevelId ?? null;

        const matchIndex = remainingWorks.findIndex((work) => {
          if (work.selectedRate?.id !== rate.rateId) return false;

          const workFeeEarnerId = work.feeEarner?.id ?? work.feeEarnerId ?? null;
          if ((rateFeeEarnerId ?? null) !== (workFeeEarnerId ?? null)) {
            return false;
          }

          if (rateFeeEarnerId !== null) {
            return true;
          }

          const workLevelId = work.feeEarnerLevel?.id ?? work.feeEarnerLevelId ?? null;
          return (rateLevelId ?? null) === (workLevelId ?? null);
        });

        if (matchIndex === -1) return undefined;
        const [matchedWork] = remainingWorks.splice(matchIndex, 1);
        return matchedWork;
      };

      const updatedTableData = topDownRates.map((rate): TableRowData => {
        const work = consumeMatchingWork(rate);
        const existingTableRow = work
          ? state.tableData.find((row) => row.workId === work.id)
          : state.tableData.find((row) => row.id === rate.id) ?? null;

        const resolvedRate = rate.rate ?? existingTableRow?.rate ?? null;
        const discount =
          work?.topDownRateDiscount ??
          existingTableRow?.rateDiscount ??
          effectiveTopDownRateDiscount;
        const hours = work?.timeSingle ?? existingTableRow?.hours ?? 0;
        const cost = work?.cost.single ?? existingTableRow?.cost ?? 0;
        const costRate = work?.costRate ?? existingTableRow?.costRate ?? 0;

        const normalisedDiscount = discount ?? 0;
        const baseRateAmount =
          resolvedRate?.amount ??
          existingTableRow?.rate?.amount ??
          (existingTableRow?.discountedRate != null && normalisedDiscount !== 100
            ? existingTableRow.discountedRate / (1 - normalisedDiscount / 100)
            : 0);
        const discountedRate = baseRateAmount * (1 - normalisedDiscount / 100);

        const feeTotal =
          work?.timeSingle != null
            ? (discountedRate * work.timeSingle) / 60
            : existingTableRow?.feeTotal ?? (discountedRate * hours) / 60;

        const percentageOfTarget =
          work?.timeSingle != null
            ? calculatePercentageOfTarget(
                work.timeSingle,
                feeTotal,
                pricingTarget,
                effectiveTopDownHoursTarget,
                effectiveTopDownFeeTarget
              )
            : existingTableRow?.percentageOfTarget ??
              calculatePercentageOfTarget(
                hours,
                feeTotal,
                pricingTarget,
                effectiveTopDownHoursTarget,
                effectiveTopDownFeeTarget
              );

        const workEffort = existingTableRow?.workEffort ?? work?.workEffort ?? undefined;
        const rateDiscount = normalisedDiscount;
        const workId = work?.id ?? existingTableRow?.workId ?? null;

        totalWeightedRate += discountedRate * hours;
        totalHours += hours;
        return {
          ...rate,
          workId,
          discountedRate,
          hours,
          percentageOfTarget,
          feeTotal,
          cost,
          costRate,
          gpm: cost ? ((feeTotal - cost) / feeTotal) * 100 : undefined,
          workEffort,
          rateDiscount,
          rate: resolvedRate ?? rate.rate,
          rateId: existingTableRow?.rateId ?? rate.rateId,
          pendingUpdate: existingTableRow?.pendingUpdate ?? false,
        };
      });

      const blendedRate = totalHours > 0 ? totalWeightedRate / totalHours : 0;
      dispatch({
        type: "APPLY_INITIAL_DATA",
        payload: {
          tableData: updatedTableData,
          blendedRate,
          topDownFeeTarget: effectiveTopDownFeeTarget,
          topDownHoursTarget: effectiveTopDownHoursTarget,
          topDownRateDiscount: effectiveTopDownRateDiscount,
          initialised: topDownRates.length > 0,
        },
      });
      prevTopDownRatesRef.current = topDownRates;
      prevTopDownWorkRef.current = topDownWork;
    }
  }, [
    topDownRates,
    topDownWork,
    topDownRateDiscount,
    pricingTarget,
    topDownFeeTarget,
    topDownHoursTarget,
    state.initialised,
  ]);

  useEffect(() => {
    initialisedRef.current = state.initialised;
  }, [state.initialised]);

  useEffect(() => {
    if (initialisedRef.current) {
      pendingTargetsRef.current = { fee: null, hours: null, rateDiscount: null };
      dispatch({ type: "RESET_FOR_REINITIALISE" });
    }
  }, [refreshTrigger, pricingTarget]);

  const debouncedDispatch = useCallback(
    debounce((action: TopDownAction) => dispatch(action), 100),
    []
  );

  const debouncedUpdateManyWorkHours = useMemo(
    () =>
      debounce(
        (
          updates: {
            workId: number;
            hours: number;
            workEffort: number;
            rateDiscount: number;
          }[]
        ) => {
          onUpdateManyWorkHours(updates);
        },
        300
      ),
    [onUpdateManyWorkHours]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateManyWorkHours.flush();
      debouncedUpdateManyWorkHours.cancel();
    };
  }, [debouncedUpdateManyWorkHours]);

  const totals = useMemo(() => {
    return state.tableData.reduce(
      (acc, row) => ({
        totalHours: acc.totalHours + row.hours,
        totalFee: acc.totalFee + row.feeTotal,
        totalPercentageOfTarget: acc.totalPercentageOfTarget + row.percentageOfTarget,
        totalCost: acc.totalCost + row.cost,
        totalWorkEffort: acc.totalWorkEffort + (row.workEffort || 0),
        totalNonPartnerEffort:
          acc.totalNonPartnerEffort + (row.rate.feeEarnerLevel?.id === 4 ? 0 : row.workEffort || 0), // TODO JS: do this properly, maybe we need a isPartner field on feeEarnerLevel that can be adjusted in settings.
        totalPartnerEffort:
          acc.totalPartnerEffort + (row.rate.feeEarnerLevel?.id === 4 ? row.workEffort || 0 : 0),
      }),
      {
        totalHours: 0,
        totalFee: 0,
        totalPercentageOfTarget: 0,
        totalCost: 0,
        totalWorkEffort: 0,
        totalNonPartnerEffort: 0,
        totalPartnerEffort: 0,
      }
    );
  }, [state.tableData]);

  const realGpm = useMemo(() => {
    if (state.topDownFeeTarget === 0) return 0;
    return ((state.topDownFeeTarget - totals.totalCost) / state.topDownFeeTarget) * 100;
  }, [state.topDownFeeTarget, totals.totalCost]);

  const { showGpm, showGpmSlider } = useGpmSettingsCheck();

  const clientRatesBoolean = topDownRates.some((rate) => rate.rate?.clientId !== null);

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      feeTarget: state.topDownFeeTarget,
      hoursTarget: state.topDownHoursTarget,
      rateDiscount: state.topDownRateDiscount,
    });
  }, [state.topDownFeeTarget, state.topDownHoursTarget, state.topDownRateDiscount, form]);

  const AdjustToTargetButton: FC<AdjustToTargetButtonProps> = ({
    pricingTarget,
    onClick,
    invert,
    style,
    additionalTooltipMessage,
  }) => {
    let increaseOrDecrease =
      state.leverageType === LeverageType.WorkEffort
        ? totals.totalWorkEffort < 100
        : totals.totalPercentageOfTarget < 100;
    increaseOrDecrease = invert ? !increaseOrDecrease : increaseOrDecrease;
    return (
      <Tooltip
        title={`${increaseOrDecrease ? "Increase" : "Decrease"} to meet target ${
          pricingTarget === PricingTarget.Hours ? "hours" : "fee"
        }${additionalTooltipMessage ? ` (${additionalTooltipMessage})` : ""}`}
      >
        {increaseOrDecrease ? (
          <UpCircleTwoTone onClick={onClick} style={style} />
        ) : (
          <DownCircleTwoTone onClick={onClick} style={style} />
        )}
      </Tooltip>
    );
  };

  const RecalculateHoursFromWorkEffortButton: FC<RecalculateHoursFromWorkEffortButtonProps> = ({
    onClick,
    style,
    tooltipMessage,
  }) => {
    return (
      <Tooltip title={tooltipMessage}>
        <RightCircleTwoTone onClick={onClick} style={style} />
      </Tooltip>
    );
  };

  const taxonomy = useTaxonomyLabel();

  const handleDeleteFeeEarnerLevel = (record: TableRowData) => {
    const worksToDelete = record.workId
      ? [record.workId]
      : topDownWork
          .filter((work) => {
            if (work.selectedRate?.id !== record.rateId) {
              return false;
            }
            const workFeeEarnerId = work.feeEarner?.id ?? work.feeEarnerId ?? null;
            const recordFeeEarnerId = record.feeEarner?.id ?? record.feeEarnerId ?? null;
            if ((recordFeeEarnerId ?? null) !== (workFeeEarnerId ?? null)) {
              return false;
            }
            if (recordFeeEarnerId !== null) {
              return true;
            }
            const workLevelId = work.feeEarnerLevel?.id ?? work.feeEarnerLevelId ?? null;
            const recordLevelId =
              record.rate.feeEarnerLevel?.id ?? record.rate.feeEarnerLevelId ?? null;
            return (recordLevelId ?? null) === (workLevelId ?? null);
          })
          .map((work) => work.id);
    onRemoveFeeEarnerLevel(record.rateId, worksToDelete);
  };

  const handleAddFeeEarnerLevel = async (feeEarnerLevelId: number) => {
    const newRate = topDownRateOptions.find(
      (rateGroup) => rateGroup.feeEarnerLevelId === feeEarnerLevelId
    )?.rates[0];
    if (newRate) {
      await onAddFeeEarnerLevel(feeEarnerLevelId);
    }
  };

  const handleAddFeeEarner = async (values: {
    feeEarnerId: number | null;
    selectedRateId: number;
    feeEarnerLevelId: number | null;
  }) => {
    console.log("values", values);
    if (onAddFeeEarner && values.selectedRateId) {
      const result = await onAddFeeEarner(
        values.feeEarnerId,
        values.feeEarnerLevelId,
        values.selectedRateId
      );
      if (result.teamMembershipId && result.work) {
        // Clear the selections after successful addition
        setSelectedFeeEarnerId(null);
        setSelectedFeeEarnerLevelId(null);
        setSelectedRateId(null);
      }
    }
  };

  const handleCascaderClear = () => {
    setSelectedFeeEarnerId(null);
    setSelectedRateId(null);
    setSelectedFeeEarnerLevelId(null);
  };

  useEffect(() => {
    const updatedRow = state.tableData.find((row) => row.pendingUpdate);
    if (updatedRow && updatedRow.workId) {
      onUpdateWork({
        workId: updatedRow.workId,
        timeSingle: Math.round(updatedRow.hours),
      });
      debouncedDispatch({ type: "CLEAR_PENDING_UPDATE", payload: { id: updatedRow.id } });
    }
  }, [state.tableData]);

  useEffect(() => {
    if (state.pendingUpdateMany) {
      const updates = state.tableData
        .map((row) =>
          row.workId
            ? {
                workId: row.workId,
                hours: Math.round(row.hours),
                workEffort: row.workEffort ?? 0,
                rateDiscount: row.rateDiscount,
              }
            : null
        )
        .filter(
          (
            row
          ): row is {
            workId: number;
            hours: number;
            workEffort: number;
            rateDiscount: number;
          } => row !== null
        );

      debouncedUpdateManyWorkHours(updates);
      dispatch({ type: "CLEAR_PENDING_UPDATE_MANY" });
    }
  }, [state.pendingUpdateMany, state.tableData, debouncedUpdateManyWorkHours]);

  const tableColumns: ColumnType<TableRowData>[] = [
    {
      title: taxonomy.FeeEarnerLevel,
      dataIndex: ["feeEarner", "feeEarnerLevel", "description"],
      key: "feeEarner.feeEarnerLevel.description",
      render: (description: string, record: TableRowData) =>
        record.feeEarner?.name ||
        record.rate.feeEarner?.name ||
        description ||
        record.rate.feeEarnerLevel?.description ||
        "N/A",
      width: 150,
    },
    {
      title: (
        <div className={rateColumnHeaderClassName}>
          <div className={rateColumnTitleClassName}>
            <span>Rate</span>
            {clientRatesBoolean && <span style={{ marginLeft: "4px" }}>*</span>}
          </div>
          {clientRatesBoolean && (
            <div className={rateColumnInfoTextClassName}>
              <i>*Client-specific rates have been selected</i>
            </div>
          )}
        </div>
      ),
      key: "rate",
      render: (record: TableRowData) => (
        <div className={rateColumnClassName}>
          <RowRateSelect
            row={record}
            currency={currency}
            matterId={matterId}
            clientId={clientId}
            organisationalUnitId={organisationalUnitId}
            topDownRateOptions={topDownRateOptions}
            className={selectClassName}
            onFeeEarnerLevelRateChange={(oldRateId, newRateId) => {
              onFeeEarnerLevelRateChange(oldRateId, newRateId, record.workId);
            }}
            onFeeEarnerRateChange={onFeeEarnerRateChange}
            onRateUpdate={(newRate) => {
              // Preserve existing metadata on the row's rate
              const mergedRate = { ...(record.rate as any), ...newRate } as Rate;
              dispatch({
                type: "UPDATE_RATE",
                payload: { id: record.id, rate: mergedRate },
              });
            }}
            onLargestRateUpdate={(ratePerMinute) => {
              dispatch({ type: "UPDATE_LARGEST_RATE_MINUTE", payload: ratePerMinute });
            }}
            currentLargestRateMinute={state.largestRateMinute}
          />
        </div>
      ),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>Hours</span>
          {totals.totalHours !== 0 ? (
            <AdjustToTargetButton
              pricingTarget={pricingTarget}
              onClick={() => dispatch({ type: "ADJUST_ALL_HOURS_TO_TARGET" })}
              style={{ marginLeft: 8 }}
              additionalTooltipMessage="pro rata"
            />
          ) : null}
        </div>
      ),
      key: "hours",
      width: 200,
      render: (_, record) => {
        const hoursToIncreaseToTarget =
          pricingTarget === PricingTarget.Hours
            ? state.topDownHoursTarget - totals.totalHours / 60
            : (state.topDownFeeTarget - totals.totalFee) / record.discountedRate;
        return (
          <div className={tableCellClassName}>
            {pricingTarget === PricingTarget.Fee &&
            state.leverageType === LeverageType.WorkEffort ? (
              decimal ? (
                <div>{Number(record.hours / 60).toFixed(1)} h</div>
              ) : (
                <div>
                  {Math.floor(record.hours / 60)} h {(record.hours % 60).toFixed(0)} min
                </div>
              )
            ) : (
              <TimeDurationInput
                defaultValue={record.hours}
                onChange={(newValue) => {
                  if (state.leverageType !== LeverageType.WorkEffort) {
                    debouncedDispatch({
                      type: "UPDATE_HOURS",
                      payload: { id: record.id, hours: newValue, workId: record.workId },
                    });
                  }
                }}
                disabled={
                  pricingTarget === PricingTarget.Fee &&
                  state.leverageType === LeverageType.WorkEffort
                }
              />
            )}
            {state.leverageType !== LeverageType.WorkEffort &&
              record.hours + 60 * hoursToIncreaseToTarget > 0 && (
                <AdjustToTargetButton
                  pricingTarget={pricingTarget}
                  onClick={() => {
                    if (Math.abs(hoursToIncreaseToTarget) >= 1) {
                      debouncedDispatch({
                        type: "UPDATE_HOURS",
                        payload: {
                          id: record.id,
                          hours: Math.round(record.hours + 60 * hoursToIncreaseToTarget),
                          workId: record.workId,
                        },
                      });
                    }
                  }}
                  style={{ paddingLeft: 10 }}
                />
              )}
          </div>
        );
      },
    },
    {
      title: `${taxonomy.FeeEarnerLevel} Discount`,
      dataIndex: "discountedRate",
      key: "discountedRate",
      width: 180,
      render: (discountedRate, record) => {
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <PercentageInput
              value={record.rateDiscount}
              min={0}
              max={99.99}
              style={{ width: "80px", marginRight: "10px" }}
              onChange={(newRateDiscount) => {
                dispatch({
                  type: "UPDATE_ROW_RATE_DISCOUNT",
                  payload: { id: record.id, rateDiscount: newRateDiscount },
                });
              }}
            />
            <span>{`${currency.symbol}${discountedRate.toFixed(2)}`}</span>
          </div>
        );
      },
    },
    ...(pricingTarget === PricingTarget.Fee
      ? [
          {
            title: (
              <div style={{ display: "flex", alignItems: "center" }}>
                <span>Leverage (hours)</span>
                <RecalculateHoursFromWorkEffortButton
                  onClick={() => {
                    dispatch({ type: "RECALCULATE_HOURS_FROM_WORK_EFFORT" });
                  }}
                  style={{ marginLeft: 8 }}
                  tooltipMessage="Recalculate hours from current leverage"
                />
              </div>
            ),
            key: "workEffort",
            width: 140,
            render: (_: any, record: TableRowData) => {
              if (pricingTarget === PricingTarget.Fee && state.topDownFeeTarget === 0) {
                return (
                  <Tooltip title={`Please set a fee target to edit this field`}>
                    <span>N/A</span>
                    <InfoCircleOutlined style={{ marginLeft: 10 }} />
                  </Tooltip>
                );
              }
              const percentageToIncrease = 100 - totals.totalWorkEffort;
              return (
                <div className={tableCellClassName}>
                  <PercentageInput
                    value={record.workEffort}
                    min={0}
                    onChange={(newWorkEffort) => {
                      debouncedDispatch({
                        type: "UPDATE_WORK_EFFORT",
                        payload: {
                          id: record.id,
                          workEffort: newWorkEffort,
                        },
                      });
                    }}
                    decimalPoints={1}
                  />
                  {(totals.totalWorkEffort < 99.9 || totals.totalWorkEffort > 100.1) &&
                    (record.workEffort || 0) + percentageToIncrease > 0 && (
                      <AdjustToTargetButton
                        pricingTarget={pricingTarget}
                        onClick={() => {
                          if (Math.abs(percentageToIncrease) >= 1) {
                            debouncedDispatch({
                              type: "UPDATE_WORK_EFFORT",
                              payload: {
                                id: record.id,
                                workEffort: Math.round(
                                  (record.workEffort || 0) + percentageToIncrease
                                ),
                              },
                            });
                          }
                        }}
                        style={{ paddingLeft: 10 }}
                      />
                    )}
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: pricingTarget === PricingTarget.Hours ? "Hours Percentage" : "Fee Percentage",
      key: "percentageOfTarget",
      width: 160,
      render: (_: any, record: TableRowData) => {
        const hoursToIncreaseToTarget =
          pricingTarget === PricingTarget.Hours
            ? state.topDownHoursTarget - totals.totalHours / 60
            : (state.topDownFeeTarget - totals.totalFee) / record.discountedRate;
        if (
          (pricingTarget === PricingTarget.Hours && state.topDownHoursTarget === 0) ||
          (pricingTarget === PricingTarget.Fee && state.topDownFeeTarget === 0)
        ) {
          return (
            <Tooltip
              title={`Please set ${
                pricingTarget === PricingTarget.Hours ? "an hours" : "a fee"
              } target to edit this field`}
            >
              <span>N/A</span>
              <InfoCircleOutlined style={{ marginLeft: 10 }} />
            </Tooltip>
          );
        }
        return (
          <div className={tableCellClassName}>
            <PercentageInput
              value={record.percentageOfTarget}
              onChange={(newPercentageOfTarget) => {
                debouncedDispatch({
                  type: "UPDATE_PERCENTAGE_OF_TARGET",
                  payload: {
                    id: record.id,
                    percentageOfTarget: newPercentageOfTarget,
                    workId: record.workId,
                  },
                });
              }}
              decimalPoints={1}
            />
            {record.hours + 60 * hoursToIncreaseToTarget > 0 && (
              <AdjustToTargetButton
                pricingTarget={pricingTarget}
                onClick={() => {
                  const hoursToIncrease =
                    pricingTarget === PricingTarget.Hours
                      ? state.topDownHoursTarget - totals.totalHours / 60
                      : (state.topDownFeeTarget - totals.totalFee) / record.discountedRate;
                  if (Math.abs(hoursToIncrease) >= 1) {
                    debouncedDispatch({
                      type: "UPDATE_HOURS",
                      payload: {
                        id: record.id,
                        hours: Math.round(record.hours + 60 * hoursToIncrease),
                        workId: record.workId,
                      },
                    });
                  }
                }}
                style={{ paddingLeft: 10 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Fee Total",
      dataIndex: "feeTotal",
      key: "feeTotal",
      width: 100,
      render: (record) => {
        return `${currency.symbol}${Number(record.toFixed(0)).toLocaleString()}`;
      },
    },
    ...(showGpm || showGpmSlider
      ? [
          {
            title: "GPM",
            dataIndex: "gpm",
            key: "gpm",
            width: !showGpm && showGpmSlider ? 223 : 80,
            render: (record: any) => {
              return record ? (
                <GpmOrSlider
                  gpm={{ fixed: record, type: GpmType.Fixed }}
                  gpmThreshold={gpmThreshold}
                  isFeeEarnerLevelGpm={false}
                  showRequired={false}
                  showGpm={showGpm}
                  showGpmSlider={showGpmSlider}
                  gpmSliderClassName={gpmSliderClassName}
                />
              ) : (
                "N/A"
              );
            },
          },
        ]
      : []),
    {
      title: "Remove",
      key: "remove",
      width: 90,
      render: (_, record) => (
        <div>
          {topDownWork.find((work) => work.id === record.workId)?.timeSingle ? ( // set this based on whether the level has any hours assigned
            <Popconfirm
              placement="right"
              title={
                <div style={{ maxWidth: "400px" }}>
                  <b>Warning:</b> This {taxonomy.FeeEarnerLevel} has hours assigned. Deleting them
                  will reset these hours to zero.
                </div>
              }
              onConfirm={(e) => {
                if (e) {
                  e.stopPropagation();
                  handleDeleteFeeEarnerLevel(record);
                }
              }}
              onCancel={(e) => {
                if (e) {
                  e.stopPropagation();
                }
              }}
              okText="OK"
              cancelText="Cancel"
            >
              <span style={{ color: "red" }}>
                <DeleteOutlined onClick={() => {}} />
              </span>
            </Popconfirm>
          ) : (
            <span style={{ color: "red" }}>
              <DeleteOutlined onClick={() => handleDeleteFeeEarnerLevel(record)} />
            </span>
          )}
        </div>
      ),
      align: "center",
    },
  ];

  const showPercentageOfTargetFocus = !(
    (pricingTarget === PricingTarget.Hours && state.topDownHoursTarget === 0) ||
    (pricingTarget === PricingTarget.Fee && state.topDownFeeTarget === 0)
  );
  const showGpmFocus = realGpm && pricingTarget === PricingTarget.Fee && (showGpm || showGpmSlider);
  const showBlendedRate = true;

  return (
    <div className={containerClassName}>
      <div
        className={headerClassName}
        style={{ display: "flex", alignItems: "baseline", gap: "32px" }}
      >
        <h3 style={{ margin: 0 }}>Top Down Pricing</h3>
      </div>
      <div className={formContainerClassName}>
        <Form
          form={form}
          initialValues={{
            feeTarget: state.topDownFeeTarget,
            hoursTarget: state.topDownHoursTarget,
            rateDiscount: state.topDownRateDiscount,
          }}
          className={formClassName}
          labelCol={{ span: 14 }}
          labelAlign="left"
        >
          <Row gutter={24}>
            <Col span={10}>
              {pricingTarget === PricingTarget.Hours ? (
                <Form.Item label="Hours target" name="hoursTarget">
                  <InputNumber
                    min={0}
                    onChange={(newHoursTarget: number | null) => {
                      pendingTargetsRef.current.hours = newHoursTarget ?? 0;
                      onTopDownHoursTargetChange(newHoursTarget);
                      dispatch({
                        type: "UPDATE_HOURS_TARGET",
                        payload: newHoursTarget ?? 0,
                      });
                    }}
                  />
                </Form.Item>
              ) : (
                <Form.Item label="Fee Target" name="feeTarget">
                  <PriceInput
                    min={0}
                    currencySymbol={currency.symbol}
                    onChange={(newFeeTarget) => {
                      pendingTargetsRef.current.fee = newFeeTarget ?? 0;
                      onTopDownFeeTargetChange(newFeeTarget);
                      dispatch({
                        type: "UPDATE_FEE_TARGET",
                        payload: newFeeTarget ?? 0,
                      });
                    }}
                    step={100}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={8}>
              {pricingTarget === PricingTarget.Hours &&
              totals.totalHours / 60 > state.topDownHoursTarget &&
              !isWithinOnePercentOfTarget(totals.totalHours / 60, state.topDownHoursTarget) ? (
                <span className={dangerClassName}>
                  <WarningOutlined style={{ marginRight: 10 }} />
                  Above hours target!
                </span>
              ) : pricingTarget === PricingTarget.Fee &&
                totals.totalFee > state.topDownFeeTarget &&
                !isWithinOnePercentOfTarget(totals.totalFee, state.topDownFeeTarget) ? (
                <span className={dangerClassName}>
                  <WarningOutlined style={{ marginRight: 10 }} /> Above fee target!
                </span>
              ) : null}
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={10}>
              <Form.Item
                label="Matter Level Discount"
                name="rateDiscount"
                valuePropName="value"
                getValueFromEvent={(value) => value}
              >
                <PercentageInput
                  min={0}
                  max={99.99}
                  style={{ width: "100%" }}
                  decimalPoints={2}
                  onChange={(newRateDiscount) => {
                    pendingTargetsRef.current.rateDiscount = newRateDiscount;
                    onTopDownRateDiscountChange(newRateDiscount);
                    debouncedDispatch({ type: "UPDATE_RATE_DISCOUNT", payload: newRateDiscount });
                  }}
                  value={state.topDownRateDiscount}
                />
              </Form.Item>
            </Col>
            <Col span={2}>
              {state.leverageType === LeverageType.FeePercentage &&
                pricingTarget === PricingTarget.Fee &&
                totals.totalPercentageOfTarget > 100 && (
                  <AdjustToTargetButton
                    pricingTarget={pricingTarget}
                    onClick={() => {
                      const newDiscount =
                        100 *
                        (1 -
                          state.topDownFeeTarget /
                            (totals.totalFee / (1 - state.topDownRateDiscount / 100)));
                      pendingTargetsRef.current.rateDiscount = newDiscount;
                      debouncedDispatch({ type: "UPDATE_RATE_DISCOUNT", payload: newDiscount });
                      onTopDownRateDiscountChange(newDiscount);
                    }}
                    invert={true}
                    style={{ paddingTop: 9, marginLeft: -15 }}
                  />
                )}
            </Col>
          </Row>
        </Form>
      </div>
      <div className={formContainerClassName}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAddRowModalVisible(true)}
          disabled={
            !feeEarnerLevels.some(
              (feeEarnerLevel) =>
                !state.tableData.some(
                  (row) =>
                    !row.feeEarner &&
                    (row.rate.feeEarnerLevelId === feeEarnerLevel.id ||
                      row.rate.feeEarnerLevel?.id === feeEarnerLevel.id)
                ) &&
                topDownRateOptions.some(
                  (rateGroup) => rateGroup.feeEarnerLevelId === feeEarnerLevel.id
                )
            ) && !onAddFeeEarner
          }
        >
          Add Resource
        </Button>
      </div>
      <div className={focusInfoContainerClassName}>
        {(showGpmFocus || showPercentageOfTargetFocus || showBlendedRate) && (
          <div className={focusInfoClassName}>
            {showPercentageOfTargetFocus ? (
              <div className={focusItemClassName}>
                {state.leverageType === LeverageType.FeePercentage ? (
                  <>
                    {pricingTarget === PricingTarget.Hours ? "Hours Percentage" : "Fee Percentage"}:
                    <span
                      className={clsx(focusNumberClassName, {
                        [goodClassName]: totals.totalPercentageOfTarget > 99,
                        [dangerClassName]: totals.totalPercentageOfTarget > 100,
                        [warningClassName]: totals.totalPercentageOfTarget <= 99,
                      })}
                    >
                      {Math.round(totals.totalPercentageOfTarget)}%
                    </span>
                  </>
                ) : (
                  <span>
                    Partner : Non-Partner Leverage: &nbsp;&nbsp;{" "}
                    {totals.totalPartnerEffort > 0 ? (
                      <b>
                        1:
                        {Number(
                          (totals.totalNonPartnerEffort / totals.totalPartnerEffort).toFixed(1)
                        )}
                      </b>
                    ) : (
                      "N/A"
                    )}
                  </span>
                )}
              </div>
            ) : null}
            {showGpmFocus ? (
              <div className={focusItemClassName}>
                GPM based on fee target:
                <GpmOrSlider
                  gpm={{ fixed: realGpm, type: GpmType.Fixed }}
                  gpmThreshold={gpmThreshold}
                  isFeeEarnerLevelGpm={false}
                  showRequired={false}
                  showGpm={showGpm}
                  showGpmSlider={showGpmSlider}
                  gpmStyle={{ marginLeft: "10px" }}
                  gpmSliderStyle={{ marginLeft: "10px", marginTop: "-3px", marginBottom: "5px" }}
                />
              </div>
            ) : null}
            {showBlendedRate ? (
              <div className={focusItemClassName}>
                Blended Rate:
                <span style={{ marginLeft: 10 }}>
                  {currency.symbol}
                  {state.blendedRate.toFixed(0)}
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div className={tableContainerClassName}>
        <Table
          loading={loadingRates}
          className={tableClassName}
          dataSource={state.tableData}
          columns={tableColumns}
          rowKey={(record) => record.id}
          pagination={false}
          tableLayout="fixed"
          summary={() => {
            const totalGpm = totals.totalCost
              ? ((totals.totalFee - totals.totalCost) / totals.totalFee) * 100
              : undefined;

            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Totals</Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <span
                    className={
                      pricingTarget === PricingTarget.Hours
                        ? isWithinOnePercentOfTarget(
                            totals.totalHours / 60,
                            state.topDownHoursTarget
                          )
                          ? goodClassName
                          : dangerClassName
                        : ""
                    }
                  >
                    {decimal ? (
                      <div>{Number(totals.totalHours / 60).toFixed(1)} h</div>
                    ) : (
                      <div>
                        {Math.floor(totals.totalHours / 60)} h {Math.round(totals.totalHours % 60)}{" "}
                        min
                      </div>
                    )}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                {pricingTarget === PricingTarget.Fee && (
                  <Table.Summary.Cell index={4}>
                    <span
                      className={clsx(focusNumberClassName, {
                        [goodClassName]:
                          totals.totalWorkEffort > 99 && totals.totalWorkEffort < 101,
                        [dangerClassName]: totals.totalWorkEffort > 101,
                        [warningClassName]: totals.totalWorkEffort <= 99,
                      })}
                    >
                      {Number(totals.totalWorkEffort.toFixed(1)) + "%"}
                    </span>
                  </Table.Summary.Cell>
                )}
                <Table.Summary.Cell index={5}>
                  {(pricingTarget === PricingTarget.Hours && state.topDownHoursTarget === 0) ||
                  (pricingTarget === PricingTarget.Fee && state.topDownFeeTarget === 0) ? (
                    <Tooltip
                      title={`Please set ${
                        pricingTarget === PricingTarget.Hours ? "an hours" : "a fee"
                      } target to see this field`}
                    >
                      <span>N/A</span>
                      <InfoCircleOutlined style={{ marginLeft: 10 }} />
                    </Tooltip>
                  ) : (
                    <span
                      className={clsx(focusNumberClassName, {
                        [goodClassName]:
                          totals.totalPercentageOfTarget > 99 &&
                          totals.totalPercentageOfTarget < 101,
                        [dangerClassName]: totals.totalPercentageOfTarget > 101,
                        [warningClassName]: totals.totalPercentageOfTarget <= 99,
                      })}
                    >
                      {Number(totals.totalPercentageOfTarget.toFixed(1)) + "%"}
                    </span>
                  )}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                  <span
                    className={clsx({
                      [goodClassName]:
                        pricingTarget === PricingTarget.Fee &&
                        isWithinOnePercentOfTarget(totals.totalFee, state.topDownFeeTarget),
                      [dangerClassName]:
                        pricingTarget === PricingTarget.Fee &&
                        !isWithinOnePercentOfTarget(totals.totalFee, state.topDownFeeTarget) &&
                        state.topDownFeeTarget - totals.totalFee < 0,
                      [warningClassName]:
                        pricingTarget === PricingTarget.Fee &&
                        !isWithinOnePercentOfTarget(totals.totalFee, state.topDownFeeTarget) &&
                        state.topDownFeeTarget - totals.totalFee > 0,
                    })}
                  >
                    {currency.symbol}
                    {Number(Math.round(totals.totalFee).toFixed(0)).toLocaleString()}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7}>
                  {totalGpm ? (
                    <GpmOrSlider
                      gpm={{ fixed: totalGpm, type: GpmType.Fixed }}
                      gpmThreshold={gpmThreshold}
                      isFeeEarnerLevelGpm={false}
                      showRequired={false}
                      showGpm={showGpm}
                      showGpmSlider={showGpmSlider}
                      gpmSliderClassName={gpmSliderClassName}
                    />
                  ) : (
                    "N/A"
                  )}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8}></Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </div>

      {/* Add Row Modal */}
      <Modal
        title="Add Row"
        open={isAddRowModalVisible}
        onCancel={() => {
          setIsAddRowModalVisible(false);
          setSelectedFeeEarnerId(null);
          setSelectedFeeEarnerLevelId(null);
          setSelectedRateId(null);
        }}
        footer={null}
        width={600}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Fee Earner Level Selection */}
          {feeEarnerLevels.some(
            (feeEarnerLevel) =>
              !state.tableData.some(
                (row) =>
                  !row.feeEarner &&
                  (row.rate.feeEarnerLevelId === feeEarnerLevel.id ||
                    row.rate.feeEarnerLevel?.id === feeEarnerLevel.id)
              ) &&
              topDownRateOptions.some(
                (rateGroup) => rateGroup.feeEarnerLevelId === feeEarnerLevel.id
              )
          ) && (
            <div>
              <div style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Add {taxonomy.FeeEarnerLevel}
              </div>
              <Select
                style={{ width: "100%" }}
                options={feeEarnerLevels
                  .filter(
                    (feeEarnerLevel) =>
                      !state.tableData.some(
                        (row) =>
                          !row.feeEarner &&
                          (row.rate.feeEarnerLevelId === feeEarnerLevel.id ||
                            row.rate.feeEarnerLevel?.id === feeEarnerLevel.id)
                      ) &&
                      topDownRateOptions.some(
                        (rateGroup) => rateGroup.feeEarnerLevelId === feeEarnerLevel.id
                      )
                  )
                  .map((feeEarnerLevel) => ({
                    label: feeEarnerLevel.description,
                    value: feeEarnerLevel.id,
                  }))}
                onChange={(feeEarnerLevelId) => {
                  if (feeEarnerLevelId) {
                    handleAddFeeEarnerLevel(feeEarnerLevelId);
                    setIsAddRowModalVisible(false);
                  }
                }}
                value={null}
                placeholder={`Select a ${taxonomy.FeeEarnerLevel}`}
              />
            </div>
          )}

          {/* Fee Earner Selection - only show if fee earner levels exist */}
          {feeEarnerLevels.length > 0 && onAddFeeEarner && (
            <div>
              <div style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Add Specific {taxonomy.FeeEarner} Rate
              </div>
              <FeeEarnerAndRatesCascaderContainer
                matterId={matterId}
                currency={currency}
                clientId={clientId}
                organisationalUnitId={organisationalUnitId}
                selectedFeeEarnerId={selectedFeeEarnerId}
                selectedFeeEarnerLevelId={selectedFeeEarnerLevelId}
                selectedRateId={selectedRateId}
                onChange={(values) => {
                  setSelectedFeeEarnerId(values.feeEarnerId);
                  setSelectedRateId(values.selectedRateId);
                  setSelectedFeeEarnerLevelId(values.feeEarnerLevelId);
                  // Only close modal and add fee earner if a rate is selected
                  if (values.selectedRateId) {
                    handleAddFeeEarner(values);
                    setIsAddRowModalVisible(false);
                  }
                }}
                onClear={handleCascaderClear}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
